import { useCallback, useEffect, useState } from "react";
import { FieldError } from "react-hook-form";
import { FormSearchDropdown } from "./FormSearchDropdown";
import { FormSearchDropdownOption, LoadOptionsCallback } from "./FormSearchDropdown/FormSearchDropdown";
import { fetchLocalities, fetchLocalityById } from "../api/locations";
import { LocalityDto } from "../models/app/location";

type LocalitySearchDropdownProps = {
    locationId: string
    value: string
    onChange: (localityId: string, option: FormSearchDropdownOption | null) => void
    error?: FieldError
    label?: string
    required?: boolean
    disabled?: boolean
    tooltip?: string
    accessToken: string | null
    isClearable?: boolean
}

export const LocalitySearchDropdown = ({
    locationId,
    value,
    onChange,
    error,
    label = "City",
    required,
    disabled,
    tooltip,
    accessToken,
    isClearable
}: LocalitySearchDropdownProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [defaultOptions, setDefaultOptions] = useState<FormSearchDropdownOption[]>([]);
    const [selectedOption, setSelectedOption] = useState<FormSearchDropdownOption | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const pageSize = 50;

    // Handle selectedOption based on value and available options
    useEffect(() => {
        const loadInitialSelectedLocality = async () => {
            // Clear selectedOption if value is empty
            if (!value) {
                setSelectedOption(null);
                return;
            }
            
            // First check if the value exists in defaultOptions
            const existingOption = defaultOptions.find(opt => opt.value === value);
            if (existingOption) {
                setSelectedOption(existingOption);
                return;
            }
            
            // If not in defaultOptions, fetch from API
            if (value && accessToken) {
                setIsLoading(true);
                try {
                    const locality = await fetchLocalityById(value, accessToken);
                    if (locality) {
                        const option = {
                            value: locality.localityId,
                            label: `${locality.locality}${locality.region ? ` (${locality.region})` : ""}`,
                            ianaId: locality.ianaId,
                        };
                        setSelectedOption(option);
                        // Only add to defaultOptions if it's not already there
                        setDefaultOptions(prev => {
                            const exists = prev.some(opt => opt.value === option.value);
                            return exists ? prev : [option, ...prev];
                        });
                    }
                } catch (error) {
                    console.error("Error fetching locality by ID:", error);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        loadInitialSelectedLocality();
    }, [locationId, value, accessToken, defaultOptions]);

    // Load initial localities for the dropdown
    useEffect(() => {
        const loadInitialOptions = async () => {
            if (!locationId || !accessToken) {
                setDefaultOptions([]);
                setSelectedOption(null);
                return;
            }

            try {
                setIsLoading(true);
                const response = await fetchLocalities(locationId, accessToken, "", 0, pageSize);
                
                const options = response.items.map((locality: LocalityDto) => ({
                    value: locality.localityId,
                    label: `${locality.locality}${locality.region ? ` (${locality.region})` : ""}`,
                    ianaId: locality.ianaId
                }));

                // Clear and set new options when location changes
                setDefaultOptions(options);
                setCurrentPage(0);
                setHasMore(response.hasNextPage);
            } catch (error) {
                console.error("Error loading initial localities:", error);
                setDefaultOptions([]);
                setSelectedOption(null);
            } finally {
                setIsLoading(false);
            }
        };

        loadInitialOptions();
    }, [locationId, accessToken]);

    const loadLocalityOptions = useCallback(async (
        inputValue: string,
        callback: LoadOptionsCallback
    ) => {
        if (!locationId || !accessToken) {
            callback([]);
            return;
        }

        try {
            setIsLoading(true);
            const response = await fetchLocalities(locationId, accessToken, inputValue, 0, pageSize);

            const options = response.items.map((locality: LocalityDto) => ({
                value: locality.localityId,
                label: `${locality.locality}${locality.region ? ` (${locality.region})` : ""}`,
                ianaId: locality.ianaId
            }));

            callback(options);
            setCurrentPage(0);
            setHasMore(response.hasNextPage);
        } catch (error) {
            console.error("Error fetching localities:", error);
            callback([]);
        } finally {
            setIsLoading(false);
        }
    }, [locationId, accessToken]);

    const handleFetchMore = useCallback(async () => {
        if (!hasMore || !locationId || isLoading || !accessToken) return;

        try {
            setIsLoading(true);
            const nextPage = currentPage + 1;
            const response = await fetchLocalities(
                locationId,
                accessToken,
                "", // Empty search term for initial load
                nextPage,
                pageSize
            );

            const newOptions = response.items.map((locality: LocalityDto) => ({
                value: locality.localityId,
                label: `${locality.locality}${locality.region ? ` (${locality.region})` : ""}`
            }));

            setDefaultOptions(prev => [...prev, ...newOptions]);
            setCurrentPage(nextPage);
            setHasMore(response.hasNextPage);
        } catch (error) {
            console.error("Error fetching more localities:", error);
        } finally {
            setIsLoading(false);
        }
    }, [locationId, accessToken, currentPage, hasMore, isLoading]);

    return (
        <FormSearchDropdown
            label={label}
            value={value}
            onChange={onChange}
            error={error}
            required={required}
            disabled={disabled || !locationId}
            tooltip={tooltip}
            loadOptions={loadLocalityOptions}
            isClearable={isClearable}
            loading={isLoading}
            defaultOptions={defaultOptions}
            fetchMore={hasMore ? handleFetchMore : undefined}
            selectedOption={selectedOption}
        />
    );
};